import type { UserListResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { defaultTableConfig } from "Infrastructure/Utilities/defaultTableConfig";
import { SettingsPaths } from "Settings/Routes/Routing";
import { useUserList } from "Settings/User/Api/Query/useUsersListQuery";
import { useAvatarFilePath } from "Settings/User/Hooks/useAvatarFilePath";
import { Edit } from "@mui/icons-material";
import { Avatar, Box, IconButton, Stack, Typography } from "@mui/material";
import { createMRTColumnHelper, type MRT_Row } from "material-react-table";
import { useNavigate } from "react-router";

type TableAction = {
	action: () => void;
	icon: JSX.Element;
};

type UserTableRow = UserListResponseDto & {
	actions?: TableAction[];
};

const avatarStyle = {
	borderRadius: "50%",
	width: "40px",
	height: "40px",
	border: "2px solid #fff",
	alignSelf: "center",
};

const userTableResource = Resources.base.user.form;
const sharedResource = Resources.base.order.shared;

export const useUsersListTable = () => {
	const { t } = useResource();
	const columnHelper = createMRTColumnHelper<UserTableRow>();
	const { data, isLoading } = useUserList();

	const navigate = useNavigate();

	const redirectToEdit = (userId: number) => {
		navigate(SettingsPaths.UsersEdit.path(userId));
	};

	const tableData = (data ?? []).map((user) => ({
		...user,
		actions: [
			{
				action: () => redirectToEdit(user.id),
				icon: <Edit />,
			},
		],
	}));

	const columns = [
		columnHelper.accessor((row) => row.name, {
			id: "name",
			header: t(userTableResource.name),
			enableColumnActions: false,
			maxSize: 60,
			grow: 0,
			minSize: 0,
			Cell: ({ row }: { row: MRT_Row<UserTableRow> }) => {
				const avatarPath = useAvatarFilePath(
					row.original?.id,
					row.original?.avatarFileName,
				);
				return (
					<Stack flexDirection="row" gap={1} alignItems="center">
						<Avatar src={avatarPath} alt="Profile image" sx={avatarStyle} />
						<Box>
							<Typography variant="tableCell" display="inline">
								{row.original.name} {row.original.surname}
							</Typography>
						</Box>
					</Stack>
				);
			},
		}),
		columnHelper.accessor((row) => row.email, {
			id: "email",
			header: t(userTableResource.email),
			enableColumnActions: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<UserTableRow> }) => (
				<Typography variant="tableCell" color="textPrimary">
					{row.original.email}
				</Typography>
			),
		}),
		columnHelper.accessor((row) => row.roles, {
			id: "roles",
			header: t(userTableResource.permissions),
			enableColumnActions: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<UserTableRow> }) => (
				<Typography variant="tableCell" color="textPrimary">
					{row.original.roles.join(", ") || "/"}
				</Typography>
			),
		}),
		{
			id: "actions",
			header: t(sharedResource.actions),
			enableColumnActions: false,
			maxSize: 60,
			grow: 0,
			Cell: ({ row }: { row: MRT_Row<UserTableRow> }) => (
				<>
					{row.original.actions?.map((action, index) => (
						<IconButton
							key={index}
							onClick={action.action}
							size="small"
							sx={{ backgroundColor: "none" }}
						>
							{action.icon}
						</IconButton>
					))}
				</>
			),
		},
	];

	const tableOptions = {
		...defaultTableConfig,
		columns,
		data: tableData,
		state: {
			showSkeletons: isLoading,
			isLoading,
		},
		muiTableBodyRowProps: ({ row }) => ({
			onDoubleClick: () => {
				const userId = row.original.id;
				navigate(SettingsPaths.UsersEdit.path(userId));
			},
		}),
	};

	return tableOptions;
};
